import { useCallback, useRef, useState } from "react";
import type { UseFormSetValue } from "react-hook-form";
import type { IntakeFormQuestion } from "@/modules/agent/requests/vendor-intake-form-response";
import { saveAssessmentIntakeQuestion } from "@/modules/third-party-risk/requests/intake-form-v2";

interface UseIntakeAutoSaveParams {
  assessmentId: string;
  setValue: UseFormSetValue<{ questions: IntakeFormQuestion[] }>;
}

export function useIntakeAutoSave({
  assessmentId,
  setValue,
}: UseIntakeAutoSaveParams) {
  const [isSaving, setIsSaving] = useState(false);
  const pendingAutoSaveCallsRef = useRef<Set<string>>(new Set());
  const debounceTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const uploadingQuestionsRef = useRef<Set<number>>(new Set());
  const fileUploadDelayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const autoSaveQuestion = useCallback(
    async (question: IntakeFormQuestion, questionIndex: number) => {
      const callId = `${question.id}-${Date.now()}`;

      try {
        pendingAutoSaveCallsRef.current.add(callId);
        setIsSaving(true);

        let sourceUrls = question.source_urls || [];
        let response: string | string[] | null = question.response || "";

        if (question.question_type === "file_upload") {
          if (Array.isArray(question.response)) {
            sourceUrls = question.response;
            response = null;
          }
        }

        await saveAssessmentIntakeQuestion(assessmentId, question.id, {
          response,
          source_urls: sourceUrls,
        });

        setValue(`questions.${questionIndex}.saveFailed` as any, false);
      } catch (error) {
        console.error("Auto-save error:", error);
        setValue(`questions.${questionIndex}.saveFailed` as any, true);
      } finally {
        pendingAutoSaveCallsRef.current.delete(callId);

        if (
          pendingAutoSaveCallsRef.current.size === 0 &&
          uploadingQuestionsRef.current.size === 0 &&
          !fileUploadDelayTimerRef.current
        ) {
          setIsSaving(false);
        }
      }
    },
    [assessmentId, setValue],
  );

  const debouncedAutoSave = useCallback(
    (question: IntakeFormQuestion, questionIndex: number) => {
      const questionId = question.id;

      const existingTimer = debounceTimersRef.current.get(questionId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      const timer = setTimeout(() => {
        void autoSaveQuestion(question, questionIndex);
        debounceTimersRef.current.delete(questionId);
      }, 1000);

      debounceTimersRef.current.set(questionId, timer);
    },
    [autoSaveQuestion],
  );

  const handleFileUploadStatusChange = useCallback(
    (questionIndex: number, isUploading: boolean) => {
      if (isUploading) {
        if (fileUploadDelayTimerRef.current) {
          clearTimeout(fileUploadDelayTimerRef.current);
          fileUploadDelayTimerRef.current = null;
        }
        uploadingQuestionsRef.current.add(questionIndex);
        setIsSaving(true);
      } else {
        uploadingQuestionsRef.current.delete(questionIndex);
        if (uploadingQuestionsRef.current.size === 0) {
          fileUploadDelayTimerRef.current = setTimeout(() => {
            if (pendingAutoSaveCallsRef.current.size === 0) {
              setIsSaving(false);
            }
            fileUploadDelayTimerRef.current = null;
          }, 2500);
        }
      }
    },
    [],
  );

  return {
    isSaving,
    debouncedAutoSave,
    handleFileUploadStatusChange,
    pendingAutoSaveCallsRef,
    uploadingQuestionsRef,
  };
}
